import { useQuery } from '@tanstack/react-query';
import { IntegrationOperation, IntegrationProvider } from '../repositories/IntegrationOperationsRepository';
import { integrationOperationsService } from '../services/IntegrationOperationsService';

/**
 * Hook to fetch SuccessWare provider information
 */
export function useSuccessWareProvider() {
  return useQuery<IntegrationProvider | null>({
    queryKey: ['integration-provider', 'successware'],
    queryFn: () => integrationOperationsService.getProviderBySlug('successware'),
    staleTime: 5 * 60 * 1000, // 5 minutes - providers rarely change
  });
}

/**
 * Hook to fetch all SuccessWare operations
 */
export function useSuccessWareOperations() {
  return useQuery<IntegrationOperation[]>({
    queryKey: ['integration-operations', 'successware'],
    queryFn: () => integrationOperationsService.getSuccessWareOperations(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch operations for a specific provider
 */
export function useOperationsByProvider(providerId: string | null | undefined) {
  return useQuery<IntegrationOperation[]>({
    queryKey: ['integration-operations', 'provider', providerId],
    queryFn: () => integrationOperationsService.getOperationsByProvider(providerId!),
    enabled: !!providerId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch a single operation by ID
 */
export function useOperation(operationId: string | null | undefined) {
  return useQuery<IntegrationOperation | null>({
    queryKey: ['integration-operation', operationId],
    queryFn: () => integrationOperationsService.getOperationById(operationId!),
    enabled: !!operationId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to search operations
 */
export function useSearchOperations(providerId: string | null | undefined, searchText: string) {
  return useQuery<IntegrationOperation[]>({
    queryKey: ['integration-operations', 'search', providerId, searchText],
    queryFn: () => integrationOperationsService.searchOperations(providerId!, searchText),
    enabled: !!providerId,
    staleTime: 2 * 60 * 1000, // 2 minutes for search results
  });
}

/**
 * Hook to fetch operations by direction (push/pull)
 */
export function useOperationsByDirection(
  providerId: string | null | undefined,
  direction: string | null | undefined
) {
  return useQuery<IntegrationOperation[]>({
    queryKey: ['integration-operations', 'direction', providerId, direction],
    queryFn: () => integrationOperationsService.getOperationsByDirection(providerId!, direction!),
    enabled: !!providerId && !!direction,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch operations by family (core/bookit)
 */
export function useOperationsByFamily(
  providerId: string | null | undefined,
  family: string | null | undefined
) {
  return useQuery<IntegrationOperation[]>({
    queryKey: ['integration-operations', 'family', providerId, family],
    queryFn: () => integrationOperationsService.getOperationsByFamily(providerId!, family!),
    enabled: !!providerId && !!family,
    staleTime: 5 * 60 * 1000,
  });
}
